import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { get, post } from '/api.js';
import { register, signIn, signOut } from '/auth.js';
import { useQrSvg } from '/qr.js';

/** The lockup. An <img> rather than inline SVG so the mark stays one file the
 *  service worker can cache and the icons are generated from. */
export function Brand() {
  return html`
    <div class="brand">
      <img src="/mark.svg" alt="" width="34" height="34" />
      <span>Cueforge</span>
    </div>`;
}

function Error_({ message }) {
  if (!message) return null;
  return html`<p class="error" data-testid="error">${message}</p>`;
}

export function SignIn({ authState, onChanged }) {
  // Arriving from a scanned invite: the code is in the URL, so open the join
  // form on it rather than making somebody find the right button first.
  const invited = new URLSearchParams(location.search).get('invite') || '';
  const [name, setName] = useState('');
  const [code, setCode] = useState(invited);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [joining, setJoining] = useState(Boolean(invited));
  const first = authState.bootstrap_needed;

  const run = (fn) => async () => {
    setBusy(true); setError('');
    try { await fn(); await onChanged(); }
    catch (e) { setError(e.detail || e.message); }
    finally { setBusy(false); }
  };

  // Claiming an unowned instance: no invite exists yet and nobody can have
  // sent you one, so the only thing to ask for is a name.
  if (first) {
    return html`
      <${Brand} />
      <p class="sub">Nobody has claimed this yet. Sign up and it is yours.</p>

      <label for="name">Your name</label>
      <input id="name" data-testid="signin-name" value=${name}
             onInput=${(e) => setName(e.target.value)} />

      <button data-testid="signin-register" disabled=${busy || !name.trim()}
              onClick=${run(() => register({ name }))}>
        Claim this instance
      </button>

      <${Error_} message=${error} />`;
  }

  // Joining is a once-ever thing; signing back in happens every time. Leading
  // with "join" sent returning users into a registration they cannot complete
  // and told them they needed an invite to their own account.
  if (joining) {
    return html`
      <${Brand} />
      <h1>Join</h1>
      <p class="sub">You will need an invite code from whoever runs this forge.</p>

      <label for="name">Your name</label>
      <input id="name" data-testid="signin-name" value=${name}
             onInput=${(e) => setName(e.target.value)} />

      <label for="code">Invite code</label>
      <input id="code" data-testid="signin-code" value=${code}
             onInput=${(e) => setCode(e.target.value)} />

      <button data-testid="signin-register"
              disabled=${busy || !name.trim() || !code.trim()}
              onClick=${run(() => register({ name, code }))}>
        Join with a passkey
      </button>

      <button class="secondary" data-testid="signin-back" disabled=${busy}
              onClick=${() => { setJoining(false); setError(''); }}>
        Back
      </button>

      <${Error_} message=${error} />`;
  }

  return html`
    <${Brand} />
    <p class="sub">Track the matches you play with your mates.</p>

    <button data-testid="signin-existing" disabled=${busy}
            onClick=${run(signIn)}>Sign in with your passkey</button>

    ${/* The phone, on a machine that is not it — GitHub issue #50. The
          browser draws the QR; all this does is ask for the cross-device
          transport FIRST, because offered third it sits under "Other
          options" and might as well not exist. Secondary, not primary: on a
          laptop that already knows you, the button above is the quick way. */''}
    <button class="secondary" data-testid="signin-phone" disabled=${busy}
            onClick=${run(() => signIn({ phone: true }))}>
      Use my phone instead
    </button>

    <button class="secondary" data-testid="signin-join-toggle" disabled=${busy}
            onClick=${() => { setJoining(true); setError(''); }}>
      I have an invite code
    </button>

    <${Error_} message=${error} />`;
}

/** An invite, as something to point a camera at.
 *
 * The QR holds a link rather than the bare code, so the other phone's own
 * camera opens the app with the invite already filled in. Building a scanner
 * would mean a camera permission and a BarcodeDetector that Safari only got
 * recently, to save typing sixteen characters that are printed underneath
 * anyway.
 */
function Invite({ code }) {
  const url = `${location.origin}/?invite=${encodeURIComponent(code)}`;
  const svg = useQrSvg(url);
  return html`
    <div class="card" data-testid="invite">
      <p class="sub" style="margin:0 0 .75rem">
        Point their camera at this. It works once, for seven days.
      </p>
      <div class="qr" data-testid="invite-qr"
           dangerouslySetInnerHTML=${{ __html: svg || '' }}></div>
      <code data-testid="invite-code">${code}</code>
    </div>`;
}

function Pending({ onGo }) {
  const [pending, setPending] = useState(
    { to_confirm: [], awaiting_them: [], disputed: [], in_progress: [] });
  const [error, setError] = useState('');

  const reload = async () => {
    try { setPending(await get('/api/me/pending')); }
    catch (e) { setError(e.detail || e.message); }
  };
  useEffect(() => { reload(); }, []);

  const act = (id, what) => async () => {
    setError('');
    try { await post(`/api/matches/${id}/${what}`); await reload(); }
    catch (e) { setError(e.detail || e.message); }
  };

  const score = (m) => `${m.mine}–${m.theirs}`;

  return html`
    ${(pending.in_progress || []).map((m) => html`
      <div class="card act" key=${m.match_id} data-testid="pending-live">
        <p class="sub" style="margin:0 0 .6rem">
          ${`Still playing ${m.opponent_name} — ${score(m)}`}
        </p>
        <button data-testid="pending-resume"
                onClick=${() => onGo('live', m.match_id)}>Back to the match</button>
      </div>`)}

    ${pending.to_confirm.length > 0 && html`
      <label>Waiting for you</label>
      ${pending.to_confirm.map((m) => html`
        <div class="card" key=${m.match_id}>
          <p class="sub" style="margin:0">
            ${m.opponent_name} recorded
            <strong data-testid="pending-score">${score(m)}</strong>
            · ${m.game_format}
          </p>
          <div class="row">
            <button data-testid="pending-confirm"
                    onClick=${act(m.match_id, 'confirm')}>That is right</button>
            <button class="secondary" data-testid="pending-dispute"
                    onClick=${act(m.match_id, 'dispute')}>Dispute</button>
          </div>
        </div>`)}`}

    ${(pending.disputed || []).length > 0 && html`
      <label>Disputed</label>
      ${pending.disputed.map((m) => html`
        <div class="card" key=${m.match_id} data-testid="pending-disputed">
          <p class="sub" style="margin:0">
            ${score(m)} v ${m.opponent_name} — someone says this is wrong, so it
            does not count. Discard it and record it again.
          </p>
          <button class="secondary" data-testid="pending-discard"
                  onClick=${act(m.match_id, 'void')}>Discard</button>
        </div>`)}`}

    ${pending.awaiting_them.length > 0 && html`
      <label>Waiting for them</label>
      ${pending.awaiting_them.map((m) => html`
        <div class="card" key=${m.match_id} data-testid="pending-awaiting">
          <p class="sub" style="margin:0">
            ${score(m)} v ${m.opponent_name} — not counted until they confirm.
          </p>
        </div>`)}`}

    <${Error_} message=${error} />`;
}

function League({ authState, onChanged }) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const linked = authState.league;

  // Two separate books, shown side by side and never merged: the league
  // rating is reproducible from the scraped archive alone.
  if (linked) {
    return html`
      <div class="card" data-testid="league-linked">
        <p class="sub" style="margin:0">League record — ${linked.name}</p>
        <div style="display:flex;justify-content:space-between;align-items:baseline">
          <strong data-testid="league-elo">${linked.elo}</strong>
          <span class="sub">
            ${linked.rank ? `#${linked.rank}` : 'unranked'} · ${linked.games} frames
          </span>
        </div>
      </div>`;
  }

  const look = async (value) => {
    setQ(value); setError('');
    if (!value.trim()) { setFound([]); return; }
    try { setFound(await get(`/api/league/search?q=${encodeURIComponent(value)}`)); }
    catch (e) { setError(e.detail || e.message); }
  };

  const link = (key) => async () => {
    setError('');
    try { await post('/api/me/league', { player_key: key }); await onChanged(); }
    catch (e) { setError(e.detail || e.message); }
  };

  if (!open) {
    return html`<button class="secondary" data-testid="league-link-open"
                        onClick=${() => setOpen(true)}>
                  Link your league record
                </button>`;
  }

  return html`
    <label>Find yourself in the league</label>
    <input data-testid="league-search" value=${q} placeholder="Your name"
           onInput=${(e) => look(e.target.value)} />
    ${found.map((p) => html`
      <div class="card" key=${p.player_key}
           style="display:flex;justify-content:space-between;align-items:center">
        <span>${p.name} <span class="sub">${p.elo}</span></span>
        <button class="secondary" data-testid="league-pick"
                style="width:auto;margin:0" onClick=${link(p.player_key)}>
          That is me
        </button>
      </div>`)}
    <${Error_} message=${error} />`;
}

export function Home({ authState, onChanged, onGo }) {
  const [invite, setInvite] = useState('');
  const [error, setError] = useState('');

  const makeInvite = async () => {
    setError('');
    try { setInvite((await post('/api/invites', {})).code); }
    catch (e) { setError(e.detail || e.message); }
  };

  return html`
    <${Brand} />
    <p class="sub" data-testid="home-greeting">Signed in as ${authState.name}</p>

    <${Pending} onGo=${onGo} />

    <button data-testid="home-record"
            onClick=${() => onGo('record')}>Record a match</button>

    <div class="row">
      <button class="secondary" data-testid="home-h2h"
              onClick=${() => onGo('h2h')}>Head to head</button>
      <button class="secondary" data-testid="home-progress"
              onClick=${() => onGo('progress')}>Your rating</button>
    </div>
    <button class="secondary" data-testid="home-board"
            onClick=${() => onGo('board')}>Leaderboard</button>

    <${League} authState=${authState} onChanged=${onChanged} />

    ${authState.is_admin && html`
      <button class="secondary" data-testid="home-invite" onClick=${makeInvite}>
        Invite someone
      </button>`}

    ${invite && html`<${Invite} code=${invite} />`}

    <button class="secondary" data-testid="home-signout"
            onClick=${async () => { await signOut(); await onChanged(); }}>
      Sign out
    </button>

    <${Error_} message=${error} />`;
}
